import { notification } from "antd";
import { call, put, select, takeEvery, takeLeading } from "redux-saga/effects";

import BoundaryLegend from "@mapmycustomers/shared/types/map/BoundaryLegend";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import i18nService from "@app/config/I18nService";
import { callApi } from "@app/store/api/callApi";
import { getAllBoundaryLegends } from "@app/store/boundaryLegends/selectors";
import { handleError } from "@app/store/errors/actions";
import { getCurrentUserId, getOrganizationId } from "@app/store/iam";
import { refreshPinLegend } from "@app/store/pinLegends/actions";
import { onRefreshPinLegend } from "@app/store/pinLegends/sagas";

import {
  createBoundaryLegend,
  deleteBoundaryLegend,
  fetchBoundaryLegends,
  updateBoundaryLegend,
} from "./actions";

export function* onFetchBoundaryLegends({
  payload,
}: ReturnType<typeof fetchBoundaryLegends.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const currentUserId: User["id"] = yield select(getCurrentUserId);

    const legends: BoundaryLegend[] = yield select(getAllBoundaryLegends);
    if (legends.length && payload?.force !== true) {
      yield put(fetchBoundaryLegends.success(legends));
      return;
    }

    const response: ListResponse<BoundaryLegend> = yield callApi("fetchBoundaryLegends", orgId, {
      // $filters: { $and: [{ userId: { $eq: currentUserId } }] },
      $limit: 1000,
    });

    yield put(
      // FIXME: should not filter here, but rather in the query, however, platform doesn't support this
      fetchBoundaryLegends.success(
        response.data.filter((legend) => !legend.default || legend.user.id === currentUserId)
      )
    );
  } catch (error) {
    yield put(fetchBoundaryLegends.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onCreateBoundaryLegend({
  payload: { callback, payload },
}: ReturnType<typeof createBoundaryLegend.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const boundaryLegend: BoundaryLegend = yield callApi("createBoundaryLegend", orgId, payload);
    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        description: intl.formatMessage({
          id: "boundaryLegends.createBoundaryLegend.success.description",
          defaultMessage: "Edit the preferences to personalize the legend.",
          description: "Text of success notification when boundary legend created successfully",
        }),
        message: intl.formatMessage({
          id: "boundaryLegends.createBoundaryLegend.success.message",
          defaultMessage: "Boundary Legend Created",
          description: "Title of success notification when boundary legend created successfully",
        }),
      });
    }
    if (callback) {
      yield call(callback, boundaryLegend);
    }
    yield put(createBoundaryLegend.success(boundaryLegend));
  } catch (error) {
    yield put(createBoundaryLegend.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateBoundaryLegend({
  payload: { callback, payload },
}: ReturnType<typeof updateBoundaryLegend.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const boundaryLegend: BoundaryLegend = yield callApi("updateBoundaryLegend", orgId, payload);
    if (callback) {
      yield call(callback, boundaryLegend);
    }
    yield put(updateBoundaryLegend.success(boundaryLegend));
  } catch (error) {
    yield put(updateBoundaryLegend.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onDeleteBoundaryLegend({
  payload: { boundaryLegendId, callback },
}: ReturnType<typeof deleteBoundaryLegend.request>) {
  const intl = i18nService.getIntl();
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("deleteBoundaryLegend", orgId, boundaryLegendId);
    if (callback) {
      yield call(callback);
    }

    if (intl) {
      notification.success({
        message: intl.formatMessage({
          id: "boundaryLegends.deleteBoundaryLegend.success",
          defaultMessage: "Successfully deleted boundary legend.",
          description: "Text of success notification when boundary legend deleted successfully",
        }),
      });
    }
    yield put(deleteBoundaryLegend.success(boundaryLegendId));
  } catch (error) {
    yield put(deleteBoundaryLegend.failure(error));
    if (intl) {
      notification.error({
        message: intl.formatMessage({
          id: "boundaryLegends.deleteBoundaryLegend.error",
          defaultMessage: "Error deleting boundary legend. Please try again or contact support.",
          description: "Text of error notification when boundary legend deleted error",
        }),
      });
    }
    yield put(handleError({ error }));
  }
}

export function* boundaryLegendsSagas() {
  yield takeLeading(fetchBoundaryLegends.request, onFetchBoundaryLegends);
  yield takeEvery(createBoundaryLegend.request, onCreateBoundaryLegend);
  yield takeEvery(updateBoundaryLegend.request, onUpdateBoundaryLegend);
  yield takeEvery(deleteBoundaryLegend.request, onDeleteBoundaryLegend);
  yield takeEvery(refreshPinLegend.request, onRefreshPinLegend);
}
